import type {
  AgendamentoNaLista,
  CardPainel,
  ColunaPainel,
  EntradaAtualizarLead,
  EntradaCriarLead,
  EntradaRegistrarInteracao,
  EntradaTransferirLead,
  EntradaTransferirLote,
  EntradaVerificarDuplicata,
  EnvelopeListagem,
  HistoricoCliente,
  ItemTimeline,
  LeadDetalhado,
  LeadNaLista,
  ResultadoCriacaoLead,
  ResultadoDuplicata,
  ResultadoInteracao,
  ResultadoTransferencia,
  ResultadoTransferenciaLote,
  Temperatura,
} from '@baiocchi/shared'

import { api, type ParametrosConsulta } from './cliente'

/**
 * `/leads`, `/clientes` e `/painel` — o núcleo (`04-leads-workflows.md`).
 *
 * O que **não** existe aqui espelha o que não existe na API, de propósito:
 *
 *  - **excluir lead** — RN-53, lead nunca é excluído;
 *  - **alterar status direto** — RN-22, a única porta é `registrarInteracao`;
 *  - **editar ou apagar interação** — RN-25, a timeline é imutável para todos os
 *    papéis, inclusive Administrador.
 *
 * Se alguma tela precisar de uma dessas, o problema é a tela.
 */
export const leadsApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<LeadNaLista>>('/leads', filtros, sinal)
  },

  detalhar(uuid: string, sinal?: AbortSignal) {
    return api.get<LeadDetalhado>(`/leads/${uuid}`, undefined, sinal)
  },

  criar(entrada: EntradaCriarLead) {
    return api.post<ResultadoCriacaoLead>('/leads', entrada)
  },

  /**
   * Só dados de classificação (ponto de conversão, produto, tags, valor).
   *
   * O tipo era `Record<string, unknown>`, o que anulava o `.strict()` de
   * `schemaAtualizarLead` do lado do cliente: campo a mais compilava e só falhava em
   * produção, com `422`. Status, responsável e temperatura continuam **fora** deste
   * corpo — cada um tem rota própria, e a temperatura é derivada (C1), nunca enviada.
   */
  atualizar(uuid: string, entrada: EntradaAtualizarLead) {
    return api.patch<LeadDetalhado>(`/leads/${uuid}`, entrada)
  },

  /** O endpoint central do sistema (§6.3). */
  registrarInteracao(uuid: string, entrada: EntradaRegistrarInteracao) {
    return api.post<ResultadoInteracao>(`/leads/${uuid}/interacoes`, entrada)
  },

  timeline(uuid: string, filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<ItemTimeline>>(`/leads/${uuid}/interacoes`, filtros, sinal)
  },

  transferir(uuid: string, entrada: EntradaTransferirLead) {
    return api.post<ResultadoTransferencia>(`/leads/${uuid}/transferir`, entrada)
  },

  assumir(uuid: string) {
    return api.post<LeadDetalhado>(`/leads/${uuid}/assumir`)
  },

  /** Gestor+ com justificativa ≥ 20 caracteres (RN-20, última linha). */
  reabrir(uuid: string, justificativa: string) {
    return api.post<LeadDetalhado>(`/leads/${uuid}/reabrir`, { justificativa })
  },

  /**
   * Faixa verde da TELA-05. A API devolve o link **e** registra
   * `WHATSAPP_ABERTO` na timeline — o registro é do servidor, para que abrir a
   * conversa por qualquer caminho conte igual.
   */
  abrirWhatsapp(uuid: string) {
    return api.post<{ link: string; registrado: boolean }>(`/leads/${uuid}/whatsapp`)
  },

  agendamentosDoLead(uuid: string) {
    return api.get<{ dados: ReadonlyArray<AgendamentoNaLista> }>(`/leads/${uuid}/agendamentos`)
  },

  transferirLote(entrada: EntradaTransferirLote) {
    return api.post<ResultadoTransferenciaLote>('/leads/lote/transferir', entrada)
  },

  mudarEquipeLote(entrada: EntradaTransferirLote) {
    return api.post<ResultadoTransferenciaLote>('/leads/lote/mudar-equipe', entrada)
  },
}

export const clientesApi = {
  /** Pré-checagem de duplicidade da TELA-03, ao sair do campo de telefone. */
  verificarDuplicata(entrada: EntradaVerificarDuplicata) {
    return api.post<ResultadoDuplicata>('/clientes/verificar-duplicata', entrada)
  },

  /** Aba "Histórico do Cliente" da TELA-05 (RN-17). */
  historico(uuid: string, sinal?: AbortSignal) {
    return api.get<HistoricoCliente>(`/clientes/${uuid}/leads`, undefined, sinal)
  },

  /**
   * TELA-06 — edição de dados cadastrais.
   *
   * ⚠️ **GAP em aberto.** `PATCH /clientes/{uuid}` está no contrato (§5) e **não**
   * existe no `clientes.controller.ts` — junto com `GET /clientes` e
   * `GET /clientes/{uuid}`, são 3 das 7 rotas da seção. A tela chama, recebe `404` e
   * mostra "recurso ainda não disponível".
   *
   * O `Record<string, unknown>` aqui é o **único** que sobrou no arquivo, e é
   * deliberado: não existe schema para tipar contra. Escrever um seria inventar o
   * contrato — quais campos são editáveis, quem pode editar cliente de lead de
   * terceiro, e como fica a unicidade de documento/e-mail do RN-10 são decisões de
   * produto e LGPD, não de implementação. **Quando a rota nascer, este tipo vira
   * `EntradaAtualizarCliente` e este comentário sai.**
   */
  atualizar(uuid: string, entrada: Record<string, unknown>) {
    return api.patch<unknown>(`/clientes/${uuid}`, entrada)
  },
}

export const painelApi = {
  colunas(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<ColunaPainel> }>('/painel/colunas', filtros, sinal)
  },

  /**
   * Uma chamada por coluna, de propósito (RNF-73: p95 < 1 s **por coluna**): a
   * tela pinta conforme chega, em vez de esperar a coluna mais cheia — que no
   * legado tem 254 cards.
   */
  cards(temperatura: Temperatura, filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeListagem<CardPainel>>(
      `/painel/colunas/${temperatura}`,
      filtros,
      sinal,
    )
  },
}
